import React, { useState } from 'react';
import {
  Activity,
  Users,
  Clock,
  TrendingUp,
  MessageSquare,
  BookOpen,
  Target,
  ArrowRight,
  Filter,
  Calendar,
  User,
  Award
} from 'lucide-react';

interface TeamActivity {
  id: string;
  type: 'achievement' | 'story' | 'learning' | 'collaboration';
  user: string;
  action: string;
  timestamp: Date;
  details?: string;
  icon: React.ReactNode;
  color: string;
}

interface TeamStory {
  id: string;
  title: string;
  author: string;
  summary: string;
  outcome: string;
  framework: string;
  timestamp: Date;
  tags: string[];
  impact: 'high' | 'medium' | 'low';
}

export const Dashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'week' | 'month' | 'quarter'>('week');
  const [activityFilter, setActivityFilter] = useState<'all' | 'achievements' | 'stories' | 'learning' | 'collaboration'>('all');

  // Mock data - in real app this would come from API
  const teamActivities: TeamActivity[] = [
    {
      id: '1',
      type: 'achievement',
      user: 'Sarah Chen',
      action: 'earned the "Framework Master" badge',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      icon: <Award className="h-4 w-4" />,
      color: 'text-[var(--mustard)]'
    },
    {
      id: '2',
      type: 'story',
      user: 'Marcus Rodriguez',
      action: 'shared a success story about using DEGAP framework',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
      details: 'Reduced project planning time by 40% using the Five Stages approach',
      icon: <MessageSquare className="h-4 w-4" />,
      color: 'text-consulting-slate-blue-600'
    },
    {
      id: '3',
      type: 'learning',
      user: 'Alex Kim',
      action: 'completed the Logical Levels learning pathway',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
      icon: <BookOpen className="h-4 w-4" />,
      color: 'text-sage-600'
    },
    {
      id: '4',
      type: 'collaboration',
      user: 'Jordan Taylor',
      action: 'collaborated on the Johari Window workshop',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000), // 8 hours ago
      details: 'Facilitated team building session with 12 participants',
      icon: <Users className="h-4 w-4" />,
      color: 'text-[var(--text-ter)]'
    },
    {
      id: '5',
      type: 'achievement',
      user: 'Casey Williams',
      action: 'unlocked the "Outcome Driver" achievement',
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000), // 12 hours ago
      icon: <Target className="h-4 w-4" />,
      color: 'text-[var(--mustard)]'
    }
  ];

  const teamStories: TeamStory[] = [
    {
      id: '1',
      title: 'Transforming Our Quarterly Planning Process',
      author: 'Sarah Chen',
      summary: 'We completely redesigned our quarterly planning using the DEGAP framework, moving from chaotic all-hands meetings to structured, outcome-focused sessions.',
      outcome: 'Reduced planning time by 60%, increased team alignment scores by 40%, and delivered all Q3 objectives on time.',
      framework: 'Five Stages of DEGAP',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
      tags: ['planning', 'team-alignment', 'efficiency'],
      impact: 'high'
    },
    {
      id: '2',
      title: 'Breaking Down Silos with Logical Levels',
      author: 'Marcus Rodriguez',
      summary: 'Used the Logical Levels framework to help two conflicting departments understand each other\'s perspectives and find common ground.',
      outcome: 'Resolved 6-month conflict, created cross-functional collaboration protocol, and improved project delivery by 35%.',
      framework: 'Logical Levels Framework',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      tags: ['conflict-resolution', 'collaboration', 'communication'],
      impact: 'high'
    },
    {
      id: '3',
      title: 'Personal Growth Through Johari Window',
      author: 'Alex Kim',
      summary: 'Applied the Johari Window model to improve self-awareness and team communication during a challenging project transition.',
      outcome: 'Increased self-awareness scores by 50%, improved team feedback quality, and successfully led team through major restructuring.',
      framework: 'Johari Window Framework',
      timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      tags: ['self-awareness', 'leadership', 'team-dynamics'],
      impact: 'medium'
    }
  ];

  const filteredActivities = teamActivities.filter(activity => {
    if (activityFilter === 'all') return true;
    return activity.type === activityFilter;
  });

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]';
      case 'medium': return 'text-[var(--mustard)] bg-[var(--mustard-dim)]';
      case 'low': return 'text-[var(--text-sec)] bg-[var(--hover-tint)]';
      default: return 'text-[var(--text-sec)] bg-[var(--hover-tint)]';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Activity className="h-12 w-12 text-consulting-slate-blue-600" />
        <div>
          <h1 className="text-4xl font-bold text-charcoal-900">Team Dashboard</h1>
          <p className="text-lg text-charcoal-600">Track team activity, stories, and learning progress</p>
        </div>
      </div>

      {/* Timeframe and Filter Controls */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-[var(--bg-raised)] border border-[var(--rule)]">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-[var(--text-ter)]" />
          <span className="text-sm font-medium text-[var(--text-sec)]">Timeframe:</span>
          <select 
            value={selectedTimeframe} 
            onChange={(e) => setSelectedTimeframe(e.target.value as any)}
            className="px-3 py-1 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-[var(--text-ter)]" />
          <span className="text-sm font-medium text-[var(--text-sec)]">Filter:</span>
          <select 
            value={activityFilter} 
            onChange={(e) => setActivityFilter(e.target.value as any)}
            className="px-3 py-1 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
          >
            <option value="all">All Activity</option>
            <option value="achievements">Achievements</option>
            <option value="stories">Stories</option>
            <option value="learning">Learning</option>
            <option value="collaboration">Collaboration</option>
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recent Team Activity */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-charcoal-900">Recent Team Activity</h2>
            <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <Activity className="h-4 w-4" />
              <span>{filteredActivities.length} activities</span>
            </div>
          </div>

          <div className="space-y-4">
            {filteredActivities.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 p-3 hover:bg-[var(--surface)] transition-colors">
                <div className={`p-2 rounded-full bg-[var(--hover-tint)] ${activity.color}`}>
                  {activity.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="h-4 w-4 text-[var(--text-ter)]" />
                    <span className="font-medium text-charcoal-900">{activity.user}</span>
                    <span className="text-charcoal-600">{activity.action}</span>
                  </div>
                  {activity.details && (
                    <p className="text-sm text-[var(--text-sec)] mb-1">{activity.details}</p>
                  )}
                  <div className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
                    <Clock className="h-3 w-3" />
                    <span>{formatTimeAgo(activity.timestamp)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-[var(--rule)]">
            <button className="w-full flex items-center justify-center gap-2 px-4 py-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 hover:bg-consulting-slate-blue-50 transition-colors">
              <span>View All Activity</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Recent Team Stories */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-charcoal-900">Recent Team Stories</h2>
            <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <MessageSquare className="h-4 w-4" />
              <span>{teamStories.length} stories</span>
            </div>
          </div>

          <div className="space-y-6">
            {teamStories.map((story) => (
              <div key={story.id} className="border border-[var(--rule)] p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-charcoal-900 text-lg leading-tight">{story.title}</h3>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getImpactColor(story.impact)}`}>
                    {story.impact} impact
                  </span>
                </div>
                
                <div className="flex items-center gap-2 mb-2 text-sm text-[var(--text-sec)]">
                  <User className="h-4 w-4" />
                  <span>{story.author}</span>
                  <span>•</span>
                  <span>{formatTimeAgo(story.timestamp)}</span>
                </div>

                <p className="text-charcoal-700 text-sm mb-3 leading-relaxed">{story.summary}</p>
                
                <div className="bg-[var(--green-dim)] border border-[var(--green)] p-3 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-[var(--green)]" />
                    <span className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">Outcome:</span>
                  </div>
                  <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">{story.outcome}</p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-consulting-slate-blue-600" />
                    <span className="text-sm font-medium text-consulting-slate-blue-700">{story.framework}</span>
                  </div>
                  <div className="flex gap-1">
                    {story.tags.map((tag) => (
                      <span key={tag} className="px-2 py-1 text-xs bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-[var(--rule)]">
            <button className="w-full flex items-center justify-center gap-2 px-4 py-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 hover:bg-consulting-slate-blue-50 transition-colors">
              <span>View All Stories</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-[var(--bg-raised)] p-6 border border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-consulting-slate-blue-100">
              <Users className="h-6 w-6 text-consulting-slate-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900">24</p>
              <p className="text-sm text-[var(--text-sec)]">Active Team Members</p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] p-6 border border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--mustard-dim)]">
              <Award className="h-6 w-6 text-[var(--mustard)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900">156</p>
              <p className="text-sm text-[var(--text-sec)]">Achievements Earned</p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] p-6 border border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sage-100">
              <BookOpen className="h-6 w-6 text-sage-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900">89</p>
              <p className="text-sm text-[var(--text-sec)]">Learning Pathways Completed</p>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] p-6 border border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[var(--green-dim)]">
              <Target className="h-6 w-6 text-[var(--green)]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-charcoal-900">42</p>
              <p className="text-sm text-[var(--text-sec)]">Success Stories Shared</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
